import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const IMG = "https://images.unsplash.com/photo-1628483211662-9bcc692c46dc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(user.role === "admin" ? "/admin" : "/account"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login" ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[80vh]" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Leather craft" className="w-full h-full object-cover" />
      </div>
      <div className="flex flex-col justify-center px-5 sm:px-14 lg:px-20 py-20">
        <p className="overline text-luxa-brown">{mode === "login" ? "Welcome back" : "Create an account"}</p>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tighter leading-none">
          {mode === "login" ? "Sign in to Trust Luxa" : "Join the atelier"}
        </h1>
        <form onSubmit={submit} className="mt-10 space-y-5 max-w-md" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label className="overline text-luxa-espresso/50">Name</Label>
              <Input data-testid="auth-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" />
            </div>
          )}
          <div>
            <Label className="overline text-luxa-espresso/50">Email</Label>
            <Input data-testid="auth-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
              className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" />
          </div>
          <div>
            <Label className="overline text-luxa-espresso/50">Password</Label>
            <Input data-testid="auth-password" type="password" required minLength={6} value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" />
          </div>
          <Button data-testid="auth-submit" type="submit" disabled={busy}
            className="w-full rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-white h-13 py-4 overline">
            {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>
        <button onClick={google} data-testid="google-login-button"
          className="mt-5 max-w-md border border-luxa-sand h-13 py-4 overline hover:border-luxa-brown transition-colors duration-300">
          Continue with Google
        </button>
        <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-8 overline text-luxa-espresso/55 link-underline self-start">
          {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const { persist } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    let done = false;
    const run = async () => {
      if (done) return;
      done = true;
      const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        persist(data);
        window.history.replaceState({}, "", "/account");
        navigate("/account", { replace: true });
      } catch { navigate("/login", { replace: true }); }
    };
    run();
    // eslint-disable-next-line
  }, []);
  return <div className="py-32 text-center overline text-luxa-espresso/40">Signing you in…</div>;
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) {
      api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/account/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    }
  }, [user, navigate]);

  if (!user) return <div className="py-32 text-center overline text-luxa-espresso/40">Loading…</div>;

  const saveAddr = async (e) => {
    e.preventDefault();
    try { await api.put("/account/addresses", addr); toast.success("Address saved"); }
    catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16" data-testid="account-page">
      <div className="flex flex-wrap gap-6 justify-between items-end">
        <div>
          <p className="overline text-luxa-brown">Your account</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tighter">{user.name}</h1>
          <p className="mt-2 text-sm text-luxa-espresso/55">{user.email}</p>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && (
            <Button asChild variant="outline" className="rounded-none h-11 overline"><Link to="/admin">Admin Panel</Link></Button>
          )}
          <Button data-testid="logout-button" onClick={logout} variant="outline" className="rounded-none h-11 overline">Sign Out</Button>
        </div>
      </div>

      <Tabs defaultValue="orders" className="mt-12">
        <TabsList className="rounded-none bg-transparent border-b border-luxa-sand w-full justify-start gap-8 p-0 h-auto">
          {[["orders", "Orders"], ["wishlist", "Wishlist"], ["address", "Addresses"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="rounded-none bg-transparent overline data-[state=active]:text-luxa-brown data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-luxa-brown pb-3 px-0">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="orders" className="mt-9">
          {orders.length === 0 && <p className="text-luxa-espresso/55" data-testid="no-orders">No orders yet.</p>}
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="bg-luxa-shell border border-luxa-sand p-6 flex flex-wrap gap-5 items-center" data-testid={`order-row-${o.id}`}>
                <div className="min-w-[140px]">
                  <p className="overline text-luxa-espresso/45">{o.created_at.slice(0, 10)}</p>
                  <p className="font-serif text-xl mt-1">{o.id}</p>
                </div>
                <div className="flex gap-2">
                  {o.items.slice(0, 3).map((i) => <img key={i.product_id} src={i.image} alt="" className="w-12 h-14 object-cover" />)}
                </div>
                <p className="overline text-luxa-brown">{o.status}</p>
                <p className="ml-auto font-serif text-xl">{money(o.total)}</p>
                <Button asChild variant="outline" className="rounded-none h-10 overline"><Link to={`/track?order=${o.id}`}>Track</Link></Button>
                <Button asChild variant="outline" className="rounded-none h-10 overline border-luxa-gold/60" data-testid={`certificate-${o.id}`}>
                  <Link to={`/certificate/${o.id}`}>Card</Link>
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="wishlist" className="mt-9">
          {wishlist.length === 0 && <p className="text-luxa-espresso/55" data-testid="no-wishlist">Nothing saved yet.</p>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {wishlist.map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="group" data-testid={`wishlist-item-${p.slug}`}>
                <div className="aspect-[4/5] overflow-hidden bg-luxa-sand"><img src={p.images?.[0]} alt={p.name} className="img-zoom w-full h-full object-cover" /></div>
                <p className="mt-3 font-serif text-lg">{p.name}</p>
                <p className="text-sm text-luxa-espresso/60">{money(p.price)}</p>
              </Link>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="address" className="mt-9">
          <div className="grid lg:grid-cols-2 gap-10">
            <form onSubmit={saveAddr} className="bg-luxa-shell border border-luxa-sand p-7 grid sm:grid-cols-2 gap-4" data-testid="address-form">
              {[["full_name", "Full name"], ["phone", "Phone"], ["line1", "Address line 1"], ["line2", "Address line 2"],
                ["city", "City"], ["state", "State"], ["postal_code", "Postal code"], ["country", "Country"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="overline text-luxa-espresso/50">{l}</Label>
                  <Input data-testid={`address-${k}`} required={k !== "line2"} value={addr[k]}
                    onChange={(e) => setAddr({ ...addr, [k]: e.target.value })}
                    className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-11" />
                </div>
              ))}
              <Button data-testid="save-address" type="submit" className="sm:col-span-2 rounded-none bg-luxa-brown text-white h-12 overline">Save Address</Button>
            </form>
            <div className="space-y-4">
              {(user.addresses || []).map((a, i) => (
                <div key={i} className="border border-luxa-sand p-6 bg-luxa-shell" data-testid={`saved-address-${i}`}>
                  <p className="font-serif text-lg">{a.full_name}</p>
                  <p className="mt-2 text-sm text-luxa-espresso/65">{a.line1} {a.line2}, {a.city} {a.state} {a.postal_code}, {a.country}</p>
                  <p className="mt-1 text-sm text-luxa-espresso/50">{a.phone}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
